import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Building2, Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, Loader2 } from 'lucide-react';
import { useSession } from '../lib/session';

export default function Login() {
  const { signIn, error } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(email, password);
    } catch {
      // The store holds the user-facing message; nothing to add here.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <motion.div
        className="login-card"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="login-head">
          <div className="login-brand">
            <div className="brand-mark">
              <Building2 size={20} strokeWidth={2.2} />
            </div>
            <div>
              <div className="brand-name">VNS ERP</div>
              <div className="brand-tag">Construction Suite</div>
            </div>
          </div>
          <h1>Sign in</h1>
          <p>Access your projects, site records, and construction ledgers</p>
        </div>

        <form className="login-body" onSubmit={onSubmit}>
          {error && (
            <motion.div
              className="alert"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          <div className="field">
            <label htmlFor="email">
              <Mail size={13} strokeWidth={2} style={{ color: 'var(--text-faint)' }} />
              <span>Email Address</span>
            </label>
            <div className="input-with-icon">
              <span className="leading-icon">
                <Mail size={15} strokeWidth={1.8} />
              </span>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="password">
              <Lock size={13} strokeWidth={2} style={{ color: 'var(--text-faint)' }} />
              <span>Password</span>
            </label>
            <div className="input-with-icon">
              <span className="leading-icon">
                <Lock size={15} strokeWidth={1.8} />
              </span>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <button
                type="button"
                className="trailing-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={15} strokeWidth={1.8} /> : <Eye size={15} strokeWidth={1.8} />}
              </button>
            </div>
          </div>

          <motion.button
            className="btn btn-primary"
            type="submit"
            disabled={busy}
            whileHover={{ scale: busy ? 1 : 1.01 }}
            whileTap={{ scale: busy ? 1 : 0.99 }}
            style={{ marginTop: 6 }}
          >
            {busy ? (
              <>
                <Loader2 size={15} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <span>Sign in</span>
                <ArrowRight size={15} strokeWidth={2} />
              </>
            )}
          </motion.button>

          <div className="hint" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <ShieldCheck size={16} strokeWidth={2} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} />
            <span>
              Secure Enterprise Portal. User access is provisioned by company administration.
            </span>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

